import {type History, type LocationDescriptorObject} from 'history';
import {useCallback, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

// The 'to' argument for navigation can be a path string or a location object.
type To = string | LocationDescriptorObject;

// The optional second argument for navigation options.
interface NavigateOptions {
  replace?: boolean;
  state?: any;
}

// The overloaded function type for the navigate function.
type NavigateFunction = {
  (to: To, options?: NavigateOptions): void;
  (delta: number): void;
};

/**
 * A simple regex to determine if a path is an absolute URL.
 * It looks for a protocol (e.g., "http:", "mailto:") or a protocol-relative URL ("//").
 */
const isAbsoluteUrl = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * A React Router v6-style `useNavigate` hook for a v5 application.
 * This provides a single `navigate` function to perform all programmatic
 * navigation actions, now with support for external URLs.
 *
 * @returns {NavigateFunction} A navigate function with an API similar to React Router v6.
 *
 * @example
 * const navigate = useNavigate();
 * navigate('/new-path'); // Pushes a new entry onto the history stack
 * navigate('https://google.com'); // Navigates to the external site
 * navigate(-1); // Goes back one entry in the history stack
 */
export function useNavigate(): NavigateFunction {
  const history = useHistory<any>();

  const navigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // Handle numeric navigation (e.g., navigate(-1) for going back)
      if (typeof to === 'number') {
        history.go(to);
        return;
      }

      const toPath = typeof to === 'string' ? to : to.pathname || '';

      // If 'to' is a string and it's an absolute URL, navigate the browser
      // directly instead of using the router's history.
      if (isAbsoluteUrl.test(toPath)) {
        if (options?.replace) {
          // oxlint-disable-next-line wandb/no-unprefixed-urls
          window.location.replace(toPath);
        } else {
          // oxlint-disable-next-line wandb/no-unprefixed-urls
          window.location.assign(toPath);
        }
        return;
      }

      // Destructure options to check for `replace` and `state`
      const {replace = false, state} = options || {};

      let location: LocationDescriptorObject;
      if (typeof to === 'string') {
        // must parse out pathname, search, and hash
        const url = new URL(to, window.location.origin);
        location = {
          pathname: url.pathname,
          search: url.search,
          hash: url.hash,
          state,
        };
      } else {
        location = {...to, state};
      }

      if (replace) {
        history.replace(location);
      } else {
        history.push(location);
      }
    },
    [history]
  );

  return navigate as NavigateFunction;
}

/**
 * A React Router v6-style `useHref` hook for a v5 application.
 * This hook takes a `to` value and returns a fully-resolved URL path.
 * It now correctly handles both internal relative paths and external absolute URLs.
 *
 * @param {To} to - The path to resolve.
 * @returns {string} The resolved URL path.
 *
 * @example
 * // If current URL is '/dashboard/settings'
 * const internalHref = useHref('../users'); // Returns '/dashboard/users'
 * const externalHref = useHref('https://google.com'); // Returns 'https://google.com'
 */
export function useHref(to: To): string {
  const history: History = useHistory();
  const {pathname: currentPathname} = useLocation();

  const href = useMemo(() => {
    const toPath = typeof to === 'string' ? to : to.pathname || '';

    // If the path is absolute, return it directly.
    if (isAbsoluteUrl.test(toPath)) {
      return toPath;
    }

    // The URL constructor needs a full base URL to resolve relative paths correctly.
    const base = window.location.origin;

    // Resolve the `to` path relative to the current full pathname.
    const resolvedUrl = new URL(toPath, `${base}${currentPathname}`);

    const locationObject: LocationDescriptorObject =
      typeof to === 'string'
        ? {
            pathname: resolvedUrl.pathname,
            search: resolvedUrl.search,
            hash: resolvedUrl.hash,
          }
        : {
            ...to,
            pathname: resolvedUrl.pathname,
            search: resolvedUrl.search,
            hash: resolvedUrl.hash,
          };

    return history.createHref(locationObject);
  }, [to, history, currentPathname]);

  return href;
}
